import { useState, useEffect } from 'react'
import AuthService from 'services/auth'
import cutileiApi from 'services/cutileiApi'
import { logApiError } from 'services/apiError'
import Dashboard from 'services/Dashboard'
import PieChart from 'components/Charts/PieChart'
import ColumnChart from 'components/Charts/ColumnChart'
import HistoryDialog from 'components/Charts/HistoryDialog'
import Select from 'components/Inputs/Select'
import { ReactComponent as Loading } from 'icons/loading2.svg'
import { DateTime } from 'luxon'

import {
  InfoContainer,
  FilterContainer,
  LoadingContainer,
  SubTitle,
  InfoText,
  LinkText
} from '../../styles'

export default function EarningsByCategory ({
  data: earnings,
  month = DateTime.now ().month,
  year = DateTime.now ().year
}) {
  const selectedDate = DateTime.local (year, month).setLocale ('pt-BR')
  const formattedDate = selectedDate.toFormat ('MMM yyyy')
  const token = AuthService.getToken ()
  const businessId = AuthService.getBusinessId ()
  const periodFilterOptions = Dashboard.PERIOD_FILTERS
  const [loading, setLoading] = useState (true)
  const [isMounted, setMounted] = useState (false)
  const [periodFilter, setPeriodFilter] = useState (null)
  const [showDetails, setShowDetails] = useState (false)
  const [earningsByMonth, setEarningsByMonth] = useState ([])

  const requestConfig = {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  }

  useEffect (() => {
    if (isMounted) setPeriodFilter (periodFilterOptions[0])
  }, [isMounted])

  useEffect (() => {
    getData ()
  }, [periodFilter])

  const getData = async () => {
    if (showDetails) try {
      setLoading (true)

      const startDate = selectedDate.minus ({ months: periodFilter.value }).startOf ('month').toISODate ()
      const endDate = selectedDate.endOf ('month').toISODate ()
      const filters = `start_date=${startDate}&end_date=${endDate}`

      const { data: earningsByCategory } = await cutileiApi.get (
        `/businesses/${businessId}/earnings_by_category?${filters}`, requestConfig
      )

      const categories = Dashboard.getKeysFromPeriod (earningsByCategory)

      setEarningsByMonth ([
        ['Categoria de serviço', ...categories],
        ...Object.values (earningsByCategory).map ((month, index) => ([
          Object.keys (earningsByCategory)[index],
          ...categories.map (name => month[name]?.totalValue || 0)
        ]))
      ])
    } catch (error) {
      logApiError (error)
    } finally {
      setLoading (false)
    }
  }

  const handlePeriodFilterSelected = option => setPeriodFilter (option)

  const toggleDetails = () => {
    if (!isMounted) setMounted (true)
    setShowDetails (!showDetails)
  }

  if (!earnings || Object.keys (earnings).length === 0) {
    return null
  }

  const earningsByCategory = [
    ['Categoria de serviço', 'Valor (R$)'],
    ...Object.keys (Object.values (earnings)[0] || {}).map (category => ([
      category,
      earnings?.[formattedDate]?.[category]?.totalValue || 0
    ]))
  ]

  return (
    <InfoContainer>
      <SubTitle>Faturamento por categoria de serviço</SubTitle>
      <PieChart data={earningsByCategory}/>
      <LinkText onClick={toggleDetails}>
        Ver histórico
      </LinkText>
      <HistoryDialog
        visible={showDetails}
        title='Faturamento por categoria de serviço'
        onClose={toggleDetails}
      >
        <FilterContainer>
          <Select
            name='periodFilter'
            placeholder='Período...'
            value={periodFilter}
            options={periodFilterOptions}
            onChange={(_, option) => handlePeriodFilterSelected (option)}
            containerStyles={{width: 250}}
          />
        </FilterContainer>
        {loading ? (
          <LoadingContainer>
            <Loading/>
            <InfoText>Gerando gráfico...</InfoText>
          </LoadingContainer>
        ) : (
          <ColumnChart data={earningsByMonth} options={{
            chartArea: {
              top: 35, left: 110, width: '100%', height: '80%'
            }
          }}/>
        )}
      </HistoryDialog>
    </InfoContainer>
  )
}
